import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MapPin, Sun } from "lucide-react";
import { api } from "@/lib/api";
import { todayISO, fmtDayLong, relDay, ORDER_TYPE } from "@/lib/format";
import { OrderStatus, Priority } from "@/components/drift/StatusBadge";
import { EmptyState, LoadingRows } from "@/components/drift/common";
import { CompanyMark } from "@/components/drift/CompanyMark";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   The technician's day. Used on /drift/montor (the real mobile route)
   and inside the phone preview in the office (embedded). Same data.
   ------------------------------------------------------------------ */

const TABS = [["idag", "I dag"], ["komande", "Komande"], ["ferdige", "Ferdige"]];

export function useMontorOrders({ mine = false, assignedTo = null }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (mine) p.set("mine", "1");
    else if (assignedTo) p.set("assigned_to", assignedTo);
    else return Promise.resolve();
    return api.get(`/drift/orders?${p.toString()}`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [mine, assignedTo]);
  useEffect(() => { setItems(null); load(); }, [load]);
  return { items, reload: load };
}

export function filterTab(items, tab, today) {
  return (items || []).filter((o) => {
    if (tab === "idag") return o.scheduled_date === today && o.status !== "fakturert" && o.status !== "ferdig";
    if (tab === "komande") return (o.scheduled_date > today || (!o.scheduled_date && o.status !== "ferdig" && o.status !== "fakturert")) && ["ny", "planlagt", "paagaar"].includes(o.status);
    return ["ferdig", "fakturert"].includes(o.status);
  }).sort((a, b) => (a.scheduled_date || "9").localeCompare(b.scheduled_date || "9") || (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
}

export function MontorFeed({ mine = false, assignedTo = null, name = "", embedded = false, linkBase = "/drift/montor/oppdrag", header = null, className }) {
  const [tab, setTab] = useState("idag");
  const { items } = useMontorOrders({ mine, assignedTo });
  const today = todayISO();
  const hour = new Date().getHours();
  const greeting = hour < 10 ? "God morgon" : hour < 17 ? "God dag" : "God kveld";
  const list = filterTab(items, tab, today);
  const counts = Object.fromEntries(TABS.map(([k]) => [k, filterTab(items, k, today).length]));
  const first = (name || "").split(" ")[0];

  return (
    <div className={cn(embedded ? "px-4 pt-3" : "px-4 pt-5", className)} data-testid={embedded ? "montor-feed-embedded" : "montor-home"} data-tab={tab}>
      <p className="eyebrow text-[12.5px]">{fmtDayLong(today)}</p>
      <h1 className={cn("mt-1 font-display font-medium tracking-[-0.025em]", embedded ? "text-[26px] leading-tight" : "text-3xl")} data-testid="montor-greeting">{greeting}{first ? `, ${first}` : ""}.</h1>
      {header}

      <div className="mt-4 grid grid-cols-3 gap-1 rounded-xl bg-secondary p-1" role="tablist" data-testid="montor-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`montor-tab-${k}`} className={cn("flex h-10 items-center justify-center gap-1.5 rounded-lg text-[13.5px] font-medium transition-[background-color,color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", tab === k ? "bg-card text-foreground shadow-[0_1px_0_hsl(var(--border)),0_1px_2px_hsla(240,6%,8%,0.06)]" : "text-muted-foreground")}>
            {l}{counts[k] > 0 && <span className={cn("tabular text-[11.5px]", tab === k ? "text-muted-foreground" : "text-muted-foreground/70")}>{counts[k]}</span>}
          </button>
        ))}
      </div>

      <div className="mt-3 space-y-2 pb-6">
        {items === null && <LoadingRows n={3} />}
        {items !== null && list.length === 0 && (
          <EmptyState compact icon={Sun} title={tab === "idag" ? "Ingen oppdrag i dag" : tab === "komande" ? "Ingen komande oppdrag" : "Ingen ferdige oppdrag"} text={tab === "idag" ? "Du er klar. Sjekk komande oppdrag eller ta kontakt med kontoret." : ""} testid="montor-empty" />
        )}
        {list.map((o) => (
          <Link key={o.id} to={`${linkBase}/${o.id}`} className="group flex items-stretch gap-3 rounded-xl bg-card p-3 shadow-panel transition-[background-color,transform] duration-150 active:scale-[0.995] active:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="montor-order-card">
            <div className="flex w-[52px] shrink-0 flex-col items-center justify-center border-r border-border/70 pr-3">
              <span className="t-num text-[17px] leading-none">{o.scheduled_time || "–"}</span>
              <span className="mt-1 text-[10.5px] text-muted-foreground">{tab === "idag" ? (o.estimated_hours ? `${o.estimated_hours} t` : "") : relDay(o.scheduled_date).slice(0, 7)}</span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2"><p className="truncate text-[14.5px] font-medium leading-tight">{o.title}</p><Priority priority={o.priority} hideNormal /></div>
              <div className="mt-1.5 flex items-center gap-2 text-[12.5px] text-muted-foreground">
                <CompanyMark name={o.customer_name} brand={o.customer_brand} type={o.customer_type} size="xs" />
                <span className="truncate">{o.customer_name}</span>
              </div>
              <p className="mt-1 flex items-center gap-1 truncate text-[12px] text-muted-foreground"><MapPin className="h-3 w-3 shrink-0" strokeWidth={1.75} aria-hidden /> <span className="truncate">{o.location_address || o.location_name}</span></p>
              <div className="mt-2 flex items-center gap-2"><OrderStatus status={o.status} size="sm" /><span className="text-[11.5px] text-muted-foreground">{ORDER_TYPE[o.type]}</span></div>
            </div>
            <ChevronRight className="my-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150 group-hover:translate-x-0.5" strokeWidth={1.75} aria-hidden />
          </Link>
        ))}
      </div>
    </div>
  );
}
